import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');

      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        setAuthLoading(false);
        return;
      }

      // Validar token com o servidor
      const isValid = await authService.validateToken();
      
      if (isValid && userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } else {
        // Token inválido, limpar dados
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      
      // Se for erro de autenticação, limpar dados
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      }
      // Para outros erros (rede), manter estado se há dados locais válidos
      else {
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setIsAuthenticated(true);
        }
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const login = async (credentials) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const response = await authService.login(credentials);
      
      // Salvar dados no localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      setUser(response.user);
      setIsAuthenticated(true);
      
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao fazer login';
      setAuthError(errorMessage);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  const register = async (userData) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const response = await authService.register(userData);
      
      // Salvar dados no localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      setUser(response.user);
      setIsAuthenticated(true);
      
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao criar conta';
      setAuthError(errorMessage);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = async () => {
    setAuthLoading(true);

    try {
      // Tentar fazer logout no servidor (não crítico se falhar)
      await authService.logout();
    } catch (error) {
      console.warn('Erro ao fazer logout no servidor:', error);
    } finally {
      // Sempre limpar dados locais
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      setAuthError(null);
      setAuthLoading(false);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshToken = async () => {
    try {
      const response = await authService.refreshToken();
      
      localStorage.setItem('token', response.token);
      if (response.user) {
        setUser(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
      }
      
      return response;
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      await logout();
      throw error;
    }
  };

  // Funções utilitárias
  const isAdmin = () => user?.isAdmin || user?.role === 'admin';
  
  const hasPermission = (permission) => {
    if (!user) return false;
    if (isAdmin()) return true;
    
    // Adicionar lógica de permissões específicas se necessário
    return false;
  };

  const getToken = () => localStorage.getItem('token');

  const value = {
    // Estado
    user,
    isAuthenticated,
    authLoading,
    authError,

    // Ações
    login,
    register,
    logout,
    updateUser,
    refreshToken,
    checkAuthStatus,

    // Utilitários
    isAdmin,
    hasPermission,
    getToken,

    // Estado de erro
    setAuthError,
    clearError: () => setAuthError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;